<template>
  <template v-if="!loading && metadataLoaded">
    <refonte-card>
      <refonte-filters-tabs>
        <template v-slot:default>
          <formations-buttons-filters v-bind:fetch="fetchDefaultData" />
        </template>
      </refonte-filters-tabs>
      <formations-filters
        v-bind:metadatas="metadatas"
        v-if="metadataLoaded" />
    </refonte-card>
    <refonte-card class="mt-[20px] !gap-0 !p-0">
      <formations-counts v-bind:total_elements="formations.length" />
      <div class="mt-2 max-h-[70vh] overflow-y-auto">
        <formations-content
          v-if="!dataLoading"
          v-bind:fetch="loadData" />
        <template v-else>
          <spinner
            sizes="w-20 h-20"
            thickness="border-8" />
        </template>
      </div>
    </refonte-card>
  </template>
  <template v-else>
    <spinner
      sizes="w-20 h-20"
      thickness="border-8" />
  </template>
</template>

<script setup lang="ts">
  import RefonteCard from '@/components/refonte/cards/refonte-card.vue';
  import { onMounted, ref, watch } from 'vue';
  import Spinner from '@/components/utils/spinner.vue';
  import { storeToRefs } from 'pinia';
  import { useRoute, useRouter } from 'vue-router';
  import api_utils from '@/mixin/api_utils';
  import RefonteFiltersTabs from '@/components/refonte/filters/refonte-filters-tabs.vue';
  import filters_utils from '@/mixin/utils/filters_utils';
  import {
    Filters,
    FormationMetadata
  } from '@/types/participant/formations-filters-types';
  import FormationsButtonsFilters from '@/views/_espace/_refonte/participant/_formations/formations-buttons-filters.vue';
  import FormationsFilters from '@/views/_espace/_refonte/participant/_formations/formations-filters.vue';
  import FormationsContent from '@/views/_espace/_refonte/participant/_formations/formations-content.vue';
  import {
    FORMATIONS_FILTERS_CONFIGURATION,
    useFormationFilterStore,
    useFormationStore
  } from '@/stores/participant/formations';
  import { API } from '@/http-common';
  import FormationsCounts from '@/views/_espace/_refonte/participant/_formations/formations-counts.vue';

  const loading = ref(false);

  const formationFilterStore = useFormationFilterStore();

  const { filters } = storeToRefs(formationFilterStore);

  const dataLoading = ref<boolean>(false);
  const formationStore = useFormationStore();
  const { formations } = storeToRefs(formationStore);
  const fetchData = async () => {
    await formationStore.fetchFormations(route.query);
  };

  const loadData = async () => {
    dataLoading.value = true;
    await fetchData();
    dataLoading.value = false;
  };
  const fetchDefaultData = async () => {
    const query = getQuery(filters.value);
    if (filters_utils.methods.isDeepEqual(query, route.query)) {
      await loadData();
      await fetchMetadata(route.query);
    }
  };

  const route = useRoute();
  watch(
    () => route.query,
    async () => {
      await initFilters();
      await loadData();
      await fetchMetadata(route.query);
    }
  );
  const initFilters = async () => {
    formationFilterStore.initFiltersFromQuery(route.query);
  };

  onMounted(async () => {
    loading.value = true;
    await initFilters();
    await fetchDefaultData();
    loading.value = false;
  });

  const router = useRouter();

  watch(filters, async (newValue, oldValue) => {
    const query = getQuery(filters.value);
    if (
      filters_utils.methods.isDeepEqual(query, route.query) &&
      query.length > 0
    ) {
      await loadData();
    } else {
      await router.push({ query });
    }
  });

  const getQuery = (filters: Filters) =>
    api_utils.methods.formatParams(filters, [
      FORMATIONS_FILTERS_CONFIGURATION.date.name,
      FORMATIONS_FILTERS_CONFIGURATION.datePeriodeDebut.name,
      FORMATIONS_FILTERS_CONFIGURATION.datePeriodeFin.name
    ]);

  const metadataLoaded = ref<boolean>(false);
  const metadatas = ref<FormationMetadata>();
  const fetchMetadata = async (query: Filters) => {
    const response = await API.get(`participant/v2/formations/metadata`, {
      params: query
    });

    if (response && response.data) {
      metadatas.value = response.data;
    }
    metadataLoaded.value = true;
  };
</script>

<style lang="scss" scoped></style>
